/**
 * Single route/navigation manifest (task 5.4 — "keep one route manifest as
 * the source for labels/icons/role-neutral destinations"). `AppShell.tsx`
 * renders grouped navigation from this data, `routes.tsx` derives its route
 * table from the same `to` values, and the mobile bottom bar reuses the same
 * destination shape — no second parallel list of routes/labels exists.
 *
 * Groups and order match design.md §3 "Navigation groups": Operación,
 * Recursos, Sistema.
 *
 * Review fix (Jose Luis Arcila's tablet-rail review pass): each destination
 * now also carries an `Icon` component (design.md §6 "Tables, forms, and
 * icons" — "a small self-hosted SVG sprite or inline React SVG components
 * with a single 1.75 px stroke vocabulary"), needed for the always-visible
 * 72px tablet compact rail (icon-only) and the desktop rail (icon+label).
 * `AppShell.tsx` is responsible for marking the rendered icon
 * `aria-hidden="true"` — icons stay decorative; the destination's visible
 * text label remains the link's accessible name.
 *
 * The icon components below are intentionally NOT exported individually —
 * this file's public surface is the `NAV_GROUPS`/`MOBILE_BOTTOM_DESTINATIONS`
 * data manifest, and the icons are an internal implementation detail of
 * building it. `eslint-plugin-react-refresh` still flags any component-shaped
 * declaration sharing a module with data exports (regardless of whether that
 * component is itself exported), which is why the rule is disabled for this
 * file rather than split into a separate icons module — this fix's stated
 * scope keeps icon components together with the manifest in `app/navigation.ts`.
 */
/* eslint-disable react-refresh/only-export-components */

import type { ComponentType, SVGProps } from "react";

export type NavGroupKey = "operacion" | "recursos" | "sistema";

/** Shared 1.75px-stroke line-icon vocabulary (design.md §6) — no remote font/icon package, no emoji. */
function iconDefaults(props: SVGProps<SVGSVGElement>): SVGProps<SVGSVGElement> {
  return {
    viewBox: "0 0 24 24",
    width: 20,
    height: 20,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    ...props,
  };
}

function InicioIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconDefaults(props)}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
    </svg>
  );
}

function CampanasIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconDefaults(props)}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function TelemetriaIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconDefaults(props)}>
      <path d="M4 19V11M10 19V5M16 19v-6M21 19H3" />
    </svg>
  );
}

function ContactosIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconDefaults(props)}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20c.7-3.8 4-6 7.5-6s6.8 2.2 7.5 6" />
    </svg>
  );
}

function PlantillasIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconDefaults(props)}>
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  );
}

/** redesign-panel-hubspot-mailgun, PR 11 (task 11.7): "Configuración" (design.md §3/§5.7) — a gear, distinct from every other destination icon. */
function ConfiguracionIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...iconDefaults(props)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M4.2 7.5l2.2 1.3M17.6 15.2l2.2 1.3M3 12h2.5M18.5 12H21M4.2 16.5l2.2-1.3M17.6 8.8l2.2-1.3" />
    </svg>
  );
}

export interface NavDestination {
  readonly key: string;
  readonly to: string;
  readonly label: string;
  // Optional override for the mobile topbar subtitle (`AppShell.tsx`'s
  // `findNavDestinationByPath` lookup) when it should differ from the
  // desktop `label`. Unused by every current destination — the reference
  // (`Panel Precision App.dc.html`) originally had Configuración's mobile
  // surfaces say "Estado" instead of "Configuración" here, but the user
  // asked for one consistent label across every surface (2026-09-18,
  // explicit twice-confirmed override — see the "sistema" group's own
  // history note above). Kept as an extension point for a future
  // destination that genuinely needs a different mobile subtitle.
  readonly topbarLabel?: string;
  readonly Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

export interface NavGroup {
  readonly key: NavGroupKey;
  readonly label: string;
  readonly destinations: readonly NavDestination[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    key: "operacion",
    label: "Operación",
    destinations: [
      { key: "inicio", to: "/", label: "Inicio", Icon: InicioIcon },
      { key: "campanas", to: "/campanas", label: "Campañas", Icon: CampanasIcon },
      { key: "telemetria", to: "/telemetria", label: "Telemetría", Icon: TelemetriaIcon },
    ],
  },
  {
    key: "recursos",
    label: "Recursos",
    // User follow-up 2026-09-22 ("ocultar el módulo de segmentos del
    // sidebar"): the `/segmentos` route itself is untouched (App.tsx defines
    // it independently of this manifest) — only its sidebar/rail entry point
    // is removed, so a direct link still works, it's just not navigable from
    // here anymore.
    destinations: [
      { key: "contactos", to: "/contactos", label: "Contactos", Icon: ContactosIcon },
      { key: "plantillas", to: "/plantillas", label: "Plantillas", Icon: PlantillasIcon },
    ],
  },
  {
    key: "sistema",
    label: "Sistema",
    // redesign-panel-hubspot-mailgun, PR 11 (task 11.7): Configuración
    // (`/configuracion`, design.md §3 + §5.7) ships with
    // `AdminOperationalHealthFunction` and its read-only "Estado operativo"
    // screen — the PR5-era placeholder empty array is now filled in.
    //
    // User follow-up 2026-09-18 (explicit override, 2nd pass — "al lado del
    // logo aún aparece como Estado"): the prototipo genuinely uses "Estado"
    // for both the mobile bottom bar (línea 1722) AND the mobile topbar
    // subtitle (`screenTitle` table, línea 2079) — a prior session even
    // reverted an earlier "unify to Estado" change specifically to restore
    // that mobile "Estado" text. The user asked for "Configuración"
    // consistently across EVERY surface instead (bottom bar, topbar
    // subtitle, and desktop sidebar all say the same thing now), a
    // conscious, twice-confirmed divergence from the reference. No
    // `topbarLabel` override means `AppShell.tsx`'s topbar subtitle lookup
    // falls back to this destination's own `label` below, same as every
    // other destination.
    // REDISENO-PANEL.md §9.3: "Estado del sistema" (antes Configuración /
    // Estado operativo) — renamed across every surface (desktop sidebar,
    // mobile "Más" sheet); the route itself stays `/configuracion`.
    destinations: [
      { key: "configuracion", to: "/configuracion", label: "Estado del sistema", Icon: ConfiguracionIcon },
    ],
  },
];

/**
 * panel-redesign-v3 T2 (REDISENO-PANEL.md §3.2): the mobile bottom bar now
 * shows 4 destinations + a 5th "Más" sheet trigger (AppShell.tsx/
 * MobileMoreSheet.tsx) — "Más" is not itself a `NavDestination` (it opens a
 * sheet, it does not navigate to its own route), so this array only ever
 * holds the 4 real destinations the sheet complements: Inicio, Campañas,
 * Telemetría, Contactos. Plantillas and Estado del sistema moved INTO the
 * "Más" sheet (previously Configuración/Estado del sistema was the 4th
 * bottom-bar slot; it and Plantillas are both one tap away via "Más" now).
 */
export const MOBILE_BOTTOM_DESTINATIONS: readonly NavDestination[] = [
  { key: "inicio", to: "/", label: "Inicio", Icon: InicioIcon },
  { key: "campanas", to: "/campanas", label: "Campañas", Icon: CampanasIcon },
  { key: "telemetria", to: "/telemetria", label: "Telemetría", Icon: TelemetriaIcon },
  { key: "contactos", to: "/contactos", label: "Contactos", Icon: ContactosIcon },
];

/** Every destination across every `NAV_GROUPS` group, in declaration order — the one place that flattens the grouped manifest for a by-key lookup. */
const ALL_NAV_DESTINATIONS: readonly NavDestination[] = NAV_GROUPS.flatMap((group) => group.destinations);

/** Looks up a `NAV_GROUPS` destination by key, throwing on a typo instead of silently building a sheet entry around `undefined`. */
function requireNavDestination(key: string): NavDestination {
  const destination = ALL_NAV_DESTINATIONS.find((candidate) => candidate.key === key);
  if (!destination) {
    throw new Error(`navigation.tsx: no NAV_GROUPS destination registered for key "${key}"`);
  }
  return destination;
}

/**
 * Destinations surfaced inside the mobile "Más" bottom sheet
 * (REDISENO-PANEL.md §3.2), each with its own subtitle for the sheet's
 * icon/subtitle/chevron row. Kept separate from `NAV_GROUPS`/
 * `MOBILE_BOTTOM_DESTINATIONS` because the sheet needs a subtitle neither of
 * those carry — but each entry's `destination` is the SAME `NAV_GROUPS`
 * object (task 5.4 "keep one route manifest as the source for labels/icons/
 * role-neutral destinations"), never a second hand-copied
 * `{ key, to, label, Icon }` literal that could silently drift from it.
 */
export const MOBILE_MORE_DESTINATIONS: readonly { destination: NavDestination; subtitle: string }[] = [
  {
    destination: requireNavDestination("plantillas"),
    subtitle: "Diseños de correo disponibles",
  },
  {
    destination: requireNavDestination("configuracion"),
    subtitle: "Integraciones y métricas disponibles",
  },
];

/**
 * Routes that are reachable and valid but intentionally have no `NAV_GROUPS`
 * entry — the same "intentionally reachable, deliberately nav-less" shape
 * `navigationRouteParity.test.ts`'s `ROUTES_WITHOUT_NAV_ENTRY` documents.
 * Record-addressed detail routes (`/campanas/:id`, `/contactos/:id`,
 * `/plantillas/:id`) still match `findNavDestinationByPath`'s `startsWith`
 * fallback onto their parent list destination, so they already get a real
 * title. `/segmentos` and `/segmentos/:segmentId` match NO nav destination
 * at all (its sidebar entry was removed outright) — without this metadata,
 * `AppShell.tsx` cannot tell them apart from a genuinely unmatched (404)
 * path, and both the topbar subtitle and the "Más" active state were
 * treating a valid screen like the 404 (Codex finding, panel-redesign-v3
 * PR #381).
 *
 * The match is exact: only the bare list path or exactly one further segment
 * (the record id) — a bare `startsWith("/segmentos/")` also matched a path
 * with EXTRA segments past the id (e.g. the malformed/unsupported
 * `/segmentos/abc/extra`), silently treating a real 404 as the valid
 * Segmentos route (Codex finding, panel-redesign-v3 PR #381). A single
 * trailing slash (`/segmentos/`, `/segmentos/:id/`) is accepted too — a
 * harmless typo/browser normalization, not a different route — but it does
 * NOT relax the "exactly one further segment" rule above.
 */
const SEGMENTOS_LIST_PATH = /^\/segmentos\/?$/;
const SEGMENTOS_DETAIL_PATH = /^\/segmentos\/[^/]+\/?$/;

const HIDDEN_VALID_ROUTES: readonly { readonly matches: (pathname: string) => boolean; readonly title: string }[] = [
  {
    matches: (pathname) => SEGMENTOS_LIST_PATH.test(pathname) || SEGMENTOS_DETAIL_PATH.test(pathname),
    title: "Segmentos",
  },
];

/** The title for a path that matches no `NAV_GROUPS` destination but is still a valid, intentionally nav-hidden route — `undefined` for a genuine 404. */
export function findHiddenRouteTitle(pathname: string): string | undefined {
  // Codex finding (panel-redesign-v3 PR #381): React Router matches every
  // route case-insensitively (none here is configured with
  // `caseSensitive`), but this regex match was case sensitive — `/Segmentos`
  // rendered the real screen while this classifier mistook it for the 404.
  const normalizedCase = pathname.toLowerCase();
  return HIDDEN_VALID_ROUTES.find((route) => route.matches(normalizedCase))?.title;
}

/**
 * `true` when the current path belongs to a destination only reachable
 * through the mobile "Más" sheet (Plantillas, Estado del sistema), or when
 * no destination matches at all AND it is not one of the `HIDDEN_VALID_ROUTES`
 * above (i.e. the real 404 route) — REDISENO-PANEL.md §3.2: "'Más' queda
 * activo en Plantillas, Estado del sistema y la 404", which never meant
 * every other nav-less route too.
 */
export function isMoreSheetDestination(pathname: string): boolean {
  const destination = findNavDestinationByPath(pathname);
  if (destination) {
    return MOBILE_MORE_DESTINATIONS.some((entry) => entry.destination.key === destination.key);
  }
  return findHiddenRouteTitle(pathname) === undefined;
}

/**
 * Destination keys that own a registered record-addressed detail route
 * (routes.tsx: `contactos/:contactId`, `plantillas/:templateId`,
 * `campanas/:campaignId`) — the only destinations `matchesDestination` below
 * allows a single extra path segment for.
 */
const DETAIL_ROUTE_KEYS = new Set(["contactos", "plantillas", "campanas"]);

/**
 * Campaign detail sub-route segments actually registered in routes.tsx
 * (`campanas/:campaignId/monitoreo`, `campanas/:campaignId/telemetria`) —
 * the only second-level segment `matchesDestination` accepts under the
 * "campanas" destination.
 */
const CAMPAIGN_SUBROUTE_SEGMENTS = new Set(["monitoreo", "telemetria"]);

/**
 * `true` when `pathname` is exactly `destination.to`, or a path depth
 * routes.tsx actually registers under it (a single record-id segment for
 * every detail-capable destination, plus the two named campaign tab
 * sub-routes). A bare `pathname.startsWith(`${destination.to}/`)` also
 * matched ANY suffix past a destination's own path — including a path no
 * route registers at all, which the router renders its `*` catch-all (404)
 * for — silently resolving it to the parent destination instead (Codex
 * finding, panel-redesign-v3 PR #381), the same shape already fixed for
 * `/segmentos` in `HIDDEN_VALID_ROUTES` above.
 *
 * Review fix (panel-redesign-v3-02): a single trailing slash on the
 * destination's own bare list path (e.g. `/contactos/`, `/campanas/`) is a
 * harmless typo/browser normalization, not a different route — same
 * tolerance `HIDDEN_VALID_ROUTES`'s `/segmentos` regexes already give it.
 * Without normalizing it first, splitting past `destination.to` produced a
 * trailing empty segment that `.filter(Boolean)` then dropped, leaving
 * `segments.length === 0` — neither the `=== 1` (id) nor `=== 2` (campaign
 * sub-route) branch accepted it, so the destination silently stopped
 * resolving (regression). Normalizing is scoped to exactly one trailing
 * slash, after the already-exact `destination.to === pathname` check above,
 * so it can only ever add tolerance for a path depth this function already
 * accepts — it never relaxes the "no extra segments" rule those branches
 * still enforce.
 */
function matchesDestination(destination: NavDestination, pathname: string): boolean {
  // Codex finding (panel-redesign-v3 PR #381): React Router matches every
  // route case-insensitively (none here is configured with
  // `caseSensitive`), but this comparison was case sensitive — a valid URL
  // like `/Contactos` rendered the real screen while this classifier
  // mistook it for the 404. Every `destination.to` in this manifest is
  // already lowercase, so lowercasing only `pathname` is enough.
  const normalizedCasePathname = pathname.toLowerCase();
  if (destination.to === normalizedCasePathname) {
    return true;
  }
  if (destination.to === "/" || !DETAIL_ROUTE_KEYS.has(destination.key)) {
    return false;
  }
  const normalizedPathname =
    normalizedCasePathname.length > 1 && normalizedCasePathname.endsWith("/")
      ? normalizedCasePathname.slice(0, -1)
      : normalizedCasePathname;
  if (destination.to === normalizedPathname) {
    return true; // e.g. /contactos/ (bare list path with a trailing slash)
  }
  if (!normalizedPathname.startsWith(`${destination.to}/`)) {
    return false;
  }
  // Codex finding (panel-redesign-v3 PR #381, fresh evidence after d3034f1):
  // a bare `.filter(Boolean)` erases an empty component a DOUBLED separator
  // produces (e.g. `/contactos//extra`), so the remainder collapsed to a
  // single "extra" segment and was accepted as a registered detail depth —
  // even though no route registers it and the real route table renders the
  // catch-all. Any empty segment anywhere in the remainder must reject the
  // match instead of silently vanishing from it.
  const segments = normalizedPathname.slice(destination.to.length + 1).split("/");
  if (segments.some((segment) => segment.length === 0)) {
    return false;
  }
  if (segments.length === 1) {
    return true; // e.g. /contactos/:id, /campanas/:id (index/Resumen tab)
  }
  return (
    destination.key === "campanas" &&
    segments.length === 2 &&
    CAMPAIGN_SUBROUTE_SEGMENTS.has(segments[1]!)
  );
}

/**
 * Resolves a destination by its route path, including nested child/detail
 * routes (02-componentes.md "Shell": "Activo (incluye rutas hijas:
 * /campanas/:id mantiene 'Campañas' activa)" — the same rule `AppShell.tsx`'s
 * `NavLink`s already apply via `end={destination.to === "/"}`). Used by
 * tests, the mobile topbar's current-screen subtitle, and any future
 * breadcrumb/title lookup so no second path→label map is created.
 */
export function findNavDestinationByPath(pathname: string): NavDestination | undefined {
  for (const group of NAV_GROUPS) {
    const found = group.destinations.find((destination) => matchesDestination(destination, pathname));
    if (found) {
      return found;
    }
  }
  return undefined;
}
